/**
 * MOCK FILTRO BOARD (TS-102) — catalogo dei campi filtrabili e valutatore del filtro.
 *
 * ## Il valutatore non conosce il catalogo
 *
 * Il backend ha bisogno del tipo del campo per sapere in che tipo Java leggere i
 * valori. Qui no: si confronta NUMERICO se entrambi i lati sono numeri, altrimenti
 * TESTO — e le date del DTO sono ISO, che in ordine lessicografico e' anche il loro
 * ordine cronologico. Sono venti righe invece di una copia del catalogo, e soprattutto
 * niente seconda tabella dei tipi da tenere allineata a mano.
 *
 * ## Si valutano i DTO, non le shape UI
 *
 * Il backend filtra sulle COLONNE, dove uno stato e' un intero mentre lato UI e' una
 * stringa ('draft'). I valori delle condizioni arrivano come interi-stringa, quindi
 * solo il DTO li confronta senza una conversione in piu'.
 */

import type {
  KanbanFilter,
  KanbanFilterCondition,
  KanbanFilterField,
} from '../../entities/kanban/kanbanMappers';
import { quotationToDto, type Quotation } from '../../entities/quotation/quotationMappers';
import { taskToDto } from '../../entities/task/taskMappers';
import type { Task } from '../../entities/task/taskTypes';
import { businessToDto, type Business } from '../../entities/business/businessMappers';
import { personToDto, type Person } from '../../entities/person/personMappers';
import { mockBusinesses } from './businessMocks';
import { mockPersons } from './personMocks';
import { mockRelationships } from './relationshipMocks';

const QUOTATION_FQN = 'it.dvel.crm.core.model.Quotation';
const PERSON_FQN = 'it.dvel.crm.core.model.Person';
const BUSINESS_FQN = 'it.dvel.crm.core.model.Business';
const TASK_FQN = 'it.dvel.crm.core.model.Task';

// ─── Catalogo ──────────────────────────────────────────────────────────
// Copia mock di KanbanFilterFieldCatalog. Etichette in italiano: il backend le
// localizza, il mock no. Se qui compare un campo che il backend non conosce, il
// filtro si compone in mock e il server lo scarta come condizione orfana.

const OPERATORS = {
  STRING: [['EQ', 'uguale a'], ['NEQ', 'diverso da'], ['CONTAINS', 'contiene'], ['NOT_CONTAINS', 'non contiene']],
  INTEGER: [['EQ', 'uguale a'], ['NEQ', 'diverso da'], ['GT', 'maggiore di'], ['GTE', 'maggiore o uguale a'], ['LT', 'minore di'], ['LTE', 'minore o uguale a'], ['BETWEEN', 'compreso tra']],
  DECIMAL: [['EQ', 'uguale a'], ['NEQ', 'diverso da'], ['GT', 'maggiore di'], ['GTE', 'maggiore o uguale a'], ['LT', 'minore di'], ['LTE', 'minore o uguale a'], ['BETWEEN', 'compreso tra']],
  MONEY: [['EQ', 'uguale a'], ['NEQ', 'diverso da'], ['GT', 'maggiore di'], ['GTE', 'maggiore o uguale a'], ['LT', 'minore di'], ['LTE', 'minore o uguale a'], ['BETWEEN', 'compreso tra']],
  DATE: [['EQ', 'uguale a'], ['NEQ', 'diverso da'], ['GT', 'maggiore di'], ['GTE', 'maggiore o uguale a'], ['LT', 'minore di'], ['LTE', 'minore o uguale a'], ['BETWEEN', 'compreso tra']],
  ENUM: [['EQ', 'uguale a'], ['NEQ', 'diverso da'], ['IN', 'tra i valori']],
} as const;

type FieldType = keyof typeof OPERATORS;

/** Le etichette che il server risolverebbe dal catalogo del contributore. */
const SCOPE_LABELS: Record<string, string> = {
  QUOTATION: 'Preventivo',
  BUSINESS: 'Azienda',
  PERSON: 'Contatto',
  // 'Task' e non 'Attivita'': e' il nome che il registro delle entita' da' al tipo, e 'Attivita''
  // e' gia' un VALORE di TaskType. Stessa scelta di kanban-filter-scope-task in crm-language.
  TASK: 'Task',
};

function field(
  scope: KanbanFilterField['scope'],
  fieldName: string,
  label: string,
  type: FieldType,
  enumValues?: { value: string; label: string }[],
): KanbanFilterField {
  return {
    scope,
    // Il server la manda gia' localizzata: il mock deve fare lo stesso, o l'editor mostrerebbe
    // "undefined ·" accanto a ogni campo e nessun test se ne accorgerebbe.
    scopeLabel: SCOPE_LABELS[scope] ?? scope,
    fieldName,
    label,
    type,
    operators: OPERATORS[type].map(([code, opLabel]) => ({
      code: code as KanbanFilterField['operators'][number]['code'],
      label: opLabel,
      cardinality: code === 'BETWEEN' ? 'RANGE' : code === 'IN' ? 'SET' : 'SINGLE',
    })),
    ...(enumValues ? { enumValues } : {}),
  };
}

// Valori enumerati come interi-stringa, come li emette il backend.
// SYNC with QuotationStatus.java: DRAFT=100 … CANCELLED=600
const QUOTATION_STATUS = [
  { value: '100', label: 'Bozza' }, { value: '200', label: 'Inviato' },
  { value: '300', label: 'Accettato' }, { value: '400', label: 'Rifiutato' },
  { value: '500', label: 'Scaduto' }, { value: '600', label: 'Annullato' },
];
// SYNC with BusinessStatus.java / PersonStatus.java: INACTIVE=0, ACTIVE=1
const ACTIVE_STATUS = [{ value: '0', label: 'Inattivo' }, { value: '1', label: 'Attivo' }];
// SYNC with PersonLeadStatus.java: NUOVO=0, IN_LAVORAZIONE=1, CHIUSO=2
const LEAD_STATUS = [
  { value: '0', label: 'Nuovo' }, { value: '1', label: 'In lavorazione' }, { value: '2', label: 'Chiuso' },
];

export const MOCK_KANBAN_FILTER_FIELDS: KanbanFilterField[] = [
  field('QUOTATION', 'title', 'Titolo', 'STRING'),
  field('QUOTATION', 'externalCode', 'Codice esterno', 'STRING'),
  field('QUOTATION', 'notes', 'Note', 'STRING'),
  field('QUOTATION', 'amount', 'Importo', 'MONEY'),
  field('QUOTATION', 'dueDate', 'Scadenza', 'DATE'),
  field('QUOTATION', 'createDate', 'Data creazione', 'DATE'),
  field('QUOTATION', 'modifiedDate', 'Ultima modifica', 'DATE'),
  field('QUOTATION', 'status', 'Stato', 'ENUM', QUOTATION_STATUS),

  field('BUSINESS', 'name', 'Nome', 'STRING'),
  field('BUSINESS', 'domain', 'Dominio', 'STRING'),
  field('BUSINESS', 'sector', 'Settore', 'STRING'),
  field('BUSINESS', 'type', 'Tipo', 'STRING'),
  field('BUSINESS', 'owner', 'Titolare', 'STRING'),
  field('BUSINESS', 'status', 'Stato', 'ENUM', ACTIVE_STATUS),

  field('PERSON', 'firstName', 'Nome', 'STRING'),
  field('PERSON', 'lastName', 'Cognome', 'STRING'),
  field('PERSON', 'location', 'Località', 'STRING'),
  field('PERSON', 'status', 'Stato', 'ENUM', ACTIVE_STATUS),
  field('PERSON', 'leadStatus', 'Stato lead', 'ENUM', LEAD_STATUS),
];

/**
 * I cataloghi del contatto e dell'azienda (TS-117), che sono **affettature per ambito** di quello
 * qui sopra e non due elenchi nuovi.
 *
 * È la traduzione letterale di cio' che fa il backend: `PersonPipelineContributor.getFilterFields()`
 * restituisce `KanbanFilterFieldCatalog.getFields(PERSON)`, cioe' esattamente le righe PERSON del
 * catalogo unico. Quelle righe esistono da prima di queste board, perche' il filtro del preventivo
 * sa saltare verso azienda cliente e contatti collegati.
 *
 * Due elenchi paralleli sarebbero divergiti, e un mock che diverge dal server e' peggio di nessun
 * mock: fa passare filtri che il server scarta come orfani, e il difetto si vede solo fuori dai mock.
 */
const byScope = (scope: KanbanFilterField['scope']): KanbanFilterField[] =>
  MOCK_KANBAN_FILTER_FIELDS.filter((f) => f.scope === scope);

export const MOCK_PERSON_FILTER_FIELDS: KanbanFilterField[] = byScope('PERSON');

export const MOCK_BUSINESS_FILTER_FIELDS: KanbanFilterField[] = byScope('BUSINESS');


// SYNC with TaskType.java: ACTIVITY=1 … LINKEDIN_CALL=7
const TASK_TYPE = [
  { value: '1', label: 'Attività operative' }, { value: '2', label: 'Chiamate' },
  { value: '3', label: 'E-mail' }, { value: '4', label: 'Riunioni' },
  { value: '5', label: 'Note' }, { value: '6', label: 'Chiamata WhatsApp' },
  { value: '7', label: 'Chiamata LinkedIn' },
];
// SYNC with TaskPriority.java: NONE=1, LOW=2, MEDIUM=3, HIGH=4
const TASK_PRIORITY = [
  { value: '1', label: 'Nessuna' }, { value: '2', label: 'Bassa' },
  { value: '3', label: 'Media' }, { value: '4', label: 'Alta' },
];
// SOLO i due valori PERSISTITI. TaskStatus ne ha cinque, ma updateStatus rifiuta gli altri tre:
// imminent/expiring_today/expired sono derivati dalla scadenza e calcolati lato client
// (taskStatus.ts). Offrirli qui darebbe un filtro valido e sempre a zero risultati, mentre la
// board mostra a video i task scaduti. Vedi TaskFilterFieldCatalog.java, stesso motivo.
const TASK_STATUS = [
  { value: '1', label: 'Da fare' }, { value: '5', label: 'Completato' },
];

/** Copia mock di TaskFilterFieldCatalog.java: un ambito solo, sette campi. */
export const MOCK_TASK_FILTER_FIELDS: KanbanFilterField[] = [
  field('TASK', 'title', 'Titolo', 'STRING'),
  field('TASK', 'expirationDate', 'Scadenza', 'DATE'),
  field('TASK', 'createDate', 'Data creazione', 'DATE'),
  field('TASK', 'modifiedDate', 'Ultima modifica', 'DATE'),
  field('TASK', 'type', 'Tipo', 'ENUM', TASK_TYPE),
  field('TASK', 'priority', 'Priorità', 'ENUM', TASK_PRIORITY),
  field('TASK', 'status', 'Stato', 'ENUM', TASK_STATUS),
];

/**
 * Catalogo DI UN TIPO (TS-117): lo dichiara il contributore, e chi non ne ha non ne ha.
 *
 * Elenco vuoto e' la risposta legittima per un tipo senza campi filtrabili — il task, finche' il
 * suo contributore non ne dichiara. Restituire comunque quello del preventivo, com'era prima,
 * avrebbe fatto comporre in mock filtri che il server rifiuta: il difetto si sarebbe visto solo
 * fuori dai mock, cioe' dove nessun test guarda.
 */
export const mockFilterFields = (className: string): KanbanFilterField[] => {
  if (className === QUOTATION_FQN) return MOCK_KANBAN_FILTER_FIELDS;
  if (className === TASK_FQN) return MOCK_TASK_FILTER_FIELDS;
  if (className === PERSON_FQN) return MOCK_PERSON_FILTER_FIELDS;
  if (className === BUSINESS_FQN) return MOCK_BUSINESS_FILTER_FIELDS;
  return pluginFilterFields.get(className) ?? [];
};

// ─── Porta per i plugin ────────────────────────────────────────────────

/** Un campo filtrabile come lo dichiara un plugin: gli operatori li aggiunge il core dal tipo. */
export interface MockFilterFieldSeed {
  fieldName: string;
  label: string;
  type: FieldType;
  enumValues?: { value: string; label: string }[];
}

const pluginFilterFields = new Map<string, KanbanFilterField[]>();

/**
 * Il catalogo dei campi filtrabili di un tipo di un PLUGIN, come lo dichiarerebbe il suo
 * contributore lato server.
 *
 * Serve perche' l'handler del catalogo sta nel core e viene prima di quelli dei plugin: MSW usa il
 * primo che combacia, quindi un plugin non puo' rispondere al posto suo, puo' solo dargli i dati.
 * Stessa porta di `registerMockBusinesses`. Va chiamata dentro il `getMockHandlers()` del plugin.
 */
export function registerMockFilterFields(
  className: string,
  scope: string,
  scopeLabel: string,
  seeds: MockFilterFieldSeed[],
): void {
  SCOPE_LABELS[scope] = scopeLabel;
  pluginFilterFields.set(
    className,
    seeds.map((s) => field(scope, s.fieldName, s.label, s.type, s.enumValues)),
  );
}

// ─── Validazione (specchio di KanbanLocalServiceImpl.validateFilter) ───

const VALUE_COUNT: Record<string, number> = { BETWEEN: 2, IN: -1 };

/** Motivo per cui il filtro non e' valido, o `null` se lo e'. Stesse regole del backend. */
export function validateKanbanFilter(
  filter: KanbanFilter | undefined, className: string): string | null {

  if (!filter?.groups?.length) return null;

  // Il catalogo contro cui si valida e' quello del TIPO della bacheca: validare un filtro di
  // task contro i campi del preventivo e' un controllo che passa e non vuol dire niente.
  const catalog = mockFilterFields(className);

  for (const group of filter.groups) {
    if (!group.conditions?.length) return 'un gruppo del filtro non ha condizioni';

    for (const c of group.conditions) {
      if (!c.scope || !c.operator || !c.fieldName) return 'una condizione del filtro e incompleta';

      const expected = VALUE_COUNT[c.operator] ?? 1;
      const count = c.values?.length ?? 0;
      if (expected < 0 ? count < 1 : count !== expected)
        return `l'operatore ${c.operator} non accetta ${count} valori`;

      // Campo in CATALOGO, e operatore retto dal suo tipo. In SCRITTURA il catalogo e'
      // quello corrente, quindi un campo che non esiste e' un client che sbaglia — non la
      // deriva che il valutatore qui sotto tollera scartando la condizione.
      const field = catalog.find(
        (f) => f.scope === c.scope && f.fieldName === c.fieldName);
      if (!field) return `il campo ${c.scope}.${c.fieldName} non e filtrabile`;
      if (!field.operators.some((o) => o.code === c.operator))
        return `il campo ${c.fieldName} non ammette l'operatore ${c.operator}`;

      // Ogni valore deve leggersi nel tipo del campo, come in KanbanFilterCriteria.isReadable.
      const unreadable = (c.values ?? []).find((v) => !isReadable(v, field.type));
      if (unreadable !== undefined)
        return `il valore ${unreadable} non e un ${field.type} valido`;
    }
  }
  return null;
}

/**
 * Specchio delle conversioni di `KanbanFilterCriteria._value`: Integer.valueOf per INTEGER ed ENUM,
 * un decimale in forma semplice per DECIMAL e MONEY (la stessa espressione di `_decimal`: niente
 * NaN, Infinity, esponenti o suffissi), LocalDate.parse (yyyy-MM-dd) per DATE.
 */
function isReadable(value: string, type: KanbanFilterField['type']): boolean {
  switch (type) {
    case 'INTEGER':
    case 'ENUM':
      return /^[+-]?\d+$/.test(value);
    case 'DECIMAL':
    case 'MONEY':
      return /^[+-]?\d+(\.\d+)?$/.test(value);
    case 'DATE': {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      // Il giorno deve esistere: «2026-02-30» ha la forma giusta e LocalDate.parse lo rifiuta.
      const date = new Date(`${value}T00:00:00Z`);
      return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
    }
    default:
      return true;
  }
}

// ─── Valutazione ───────────────────────────────────────────────────────

type Dto = Record<string, unknown>;

/** Confronto: numerico se entrambi i lati sono numeri, altrimenti testo. */
function cmp(actual: unknown, expected: string): number {
  const a = Number(actual);
  const b = Number(expected);
  if (!Number.isNaN(a) && !Number.isNaN(b) && actual !== '' && actual !== null)
    return a - b;
  return String(actual ?? '').localeCompare(expected, undefined, { sensitivity: 'base' });
}

const has = (actual: unknown, needle: string) =>
  String(actual ?? '').toLowerCase().includes(needle.toLowerCase());

function matches(
  record: Dto | undefined, c: KanbanFilterCondition, catalog: KanbanFilterField[]): boolean {
  // Condizione ORFANA (campo non piu' in catalogo): si SCARTA, cioe' esce dalla
  // congiunzione del gruppo — che e' quanto dire "vera". Stessa scelta del traduttore
  // lato server, e va tenuta identica: se il mock restringesse dove il server allarga,
  // in mock mode si vedrebbe un difetto che in produzione non c'e'.
  //
  // Il CATALOGO arriva da fuori perche' e' del TIPO: con quello del preventivo cablato qui,
  // ogni condizione di una board di task sarebbe risultata orfana, e il filtro dei task
  // avrebbe mostrato tutto senza dirlo.
  //
  // Il controllo viene PRIMA di quello sul record, o un'orfana su scope azienda per un
  // preventivo senza azienda cadrebbe come "record assente" e non come orfana, con
  // l'esito opposto.
  if (!catalog.some((f) => f.scope === c.scope && f.fieldName === c.fieldName))
    return true;

  // Nessun record da confrontare (preventivo senza azienda, o senza contatti
  // collegati): non soddisfatta. Il preventivo non entra per sbaglio.
  if (!record) return false;

  const actual = record[c.fieldName];
  const [first, second] = c.values ?? [];

  switch (c.operator) {
    case 'EQ': return cmp(actual, first) === 0;
    case 'NEQ': return cmp(actual, first) !== 0;
    case 'GT': return cmp(actual, first) > 0;
    case 'GTE': return cmp(actual, first) >= 0;
    case 'LT': return cmp(actual, first) < 0;
    case 'LTE': return cmp(actual, first) <= 0;
    case 'BETWEEN': return cmp(actual, first) >= 0 && cmp(actual, second) <= 0;
    case 'CONTAINS': return has(actual, first);
    case 'NOT_CONTAINS': return !has(actual, first);
    case 'IN': return (c.values ?? []).some((v) => cmp(actual, v) === 0);
    default: return false;
  }
}

/** Contatti collegati al preventivo, in ENTRAMBI i versi della relazione. */
function relatedPersons(quotationId: number): Dto[] {
  const ids = new Set<number>();

  for (const r of mockRelationships) {
    if (r.className1 === QUOTATION_FQN && r.classPK1 === quotationId && r.className2 === PERSON_FQN)
      ids.add(r.classPK2);
    if (r.className2 === QUOTATION_FQN && r.classPK2 === quotationId && r.className1 === PERSON_FQN)
      ids.add(r.classPK1);
  }

  return mockPersons.filter((p) => ids.has(p.personId)).map((p) => personToDto(p) as unknown as Dto);
}

/**
 * Vero se il preventivo supera il filtro: gruppi in OR, condizioni in AND. Filtro
 * assente o senza gruppi = nessun restringimento.
 */
export function matchesKanbanFilter(
  quotation: Quotation,
  filter: KanbanFilter | null | undefined,
): boolean {
  if (!filter?.groups?.length) return true;

  const quotationDto = quotationToDto(quotation) as unknown as Dto;
  const business = mockBusinesses.find((b) => b.businessId === quotation.businessId);
  const businessDto = business ? (businessToDto(business) as unknown as Dto) : undefined;
  // I contatti si risolvono solo se servono: e' il salto piu' costoso.
  let persons: Dto[] | undefined;

  return filter.groups.some((group) =>
    group.conditions.every((c) => {
      if (c.scope === 'QUOTATION') return matches(quotationDto, c, MOCK_KANBAN_FILTER_FIELDS);
      if (c.scope === 'BUSINESS') return matches(businessDto, c, MOCK_KANBAN_FILTER_FIELDS);
      persons ??= relatedPersons(quotation.quotationId);
      return persons.some((p) => matches(p, c, MOCK_KANBAN_FILTER_FIELDS));
    }),
  );
}

/**
 * Il valutatore di un tipo che dichiara **un ambito solo** — task, contatto, azienda.
 *
 * Non e' la funzione del preventivo con due righe in meno: quella che manca e' la risoluzione dei
 * record collegati, che questi tipi non hanno. Il resto — gruppi in OR, condizioni in AND, confronto
 * — e' `matches`, che e' uno solo. Stessa divisione del backend, dove `KanbanFilterCriteria` e'
 * condiviso e cambia solo lo `scopeResolver`.
 *
 * Una condizione di un ambito diverso puo' arrivare qui solo da un filtro salvato quando il catalogo
 * era diverso, oppure da un filtro composto su una board di preventivi — ed e' orfana rispetto a
 * questo catalogo, quindi `matches` la scarta da se'.
 */
const monoScopeMatcher =
  <T>(toDto: (record: T) => unknown, catalog: KanbanFilterField[]) =>
  (record: T, filter: KanbanFilter | null | undefined): boolean => {
    if (!filter?.groups?.length) return true;

    const dto = toDto(record) as Dto;

    return filter.groups.some((group) =>
      group.conditions.every((c) => matches(dto, c, catalog)),
    );
  };

/** Vero se il TASK supera il filtro. Vedi {@link monoScopeMatcher}. */
export const matchesTaskKanbanFilter: (t: Task, f: KanbanFilter | null | undefined) => boolean =
  monoScopeMatcher(taskToDto, MOCK_TASK_FILTER_FIELDS);

/** Vero se il CONTATTO supera il filtro. Vedi {@link monoScopeMatcher}. */
export const matchesPersonKanbanFilter: (p: Person, f: KanbanFilter | null | undefined) => boolean =
  monoScopeMatcher(personToDto, MOCK_PERSON_FILTER_FIELDS);

/** Vero se l'AZIENDA supera il filtro. Vedi {@link monoScopeMatcher}. */
export const matchesBusinessKanbanFilter: (
  b: Business,
  f: KanbanFilter | null | undefined,
) => boolean = monoScopeMatcher(businessToDto, MOCK_BUSINESS_FILTER_FIELDS);

/**
 * Vero se il record di un tipo di un PLUGIN supera il filtro, contro il catalogo registrato con
 * {@link registerMockFilterFields}. Il record e' il DTO del plugin com'e': i campi si leggono per
 * nome, come fa il traduttore lato server. Vedi {@link monoScopeMatcher}.
 */
export function matchesMockKanbanFilter(
  record: Record<string, unknown>,
  filter: KanbanFilter | null | undefined,
  className: string,
): boolean {
  return monoScopeMatcher((r: Record<string, unknown>) => r, mockFilterFields(className))(
    record,
    filter,
  );
}
